import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Pause, Play, MoonStar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { logUsageEvent } from '../lib/logger';
import { ehComercialInterno as trabalhaNoRodizio } from '../lib/departamentos';
import { paraLocalBRT } from '../lib/escalaDisponibilidade';
import { estadoSla, pausaAberta, minutosPausadosNoDia, fmtDuracaoMin, EstadoSla } from '../lib/pausaSla';
import type { AusenciaTrabalho, HorarioTrabalho } from '../../types';

/**
 * Botão "Ativa / Inativa" do topo — só para quem tem SLA de atendimento medido
 * (Closers e BDRs; ver onde é montado em App.tsx).
 *
 * Marcar-se inativa grava uma pausa (ausencias_trabalho origem='pausa') e o
 * relógio do SLA para: estaDisponivel e tempoUtilDecorrido já descontam
 * ausências parciais, então o ranking, a média e o painel de leads parados
 * passam a ignorar esse intervalo sozinhos. Marcar-se ativa fecha a pausa.
 *
 * Busca os próprios dados em vez de receber por prop: o botão vive no header,
 * que não conhece escala de ninguém. Também decide sozinho se aparece — mesmo
 * padrão do LeadAssignedPopup, pra App.tsx não virar uma lista de regras de
 * departamento.
 */
interface PausaSlaToggleProps {
    /** No header o rótulo só aparece em telas largas (espaço); no menu mobile,
     *  onde o ícone sozinho não se explica, ele fica sempre visível. */
    mostrarRotulo?: boolean;
}

const PausaSlaToggle: React.FC<PausaSlaToggleProps> = ({ mostrarRotulo = false }) => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    // Closers e BDRs: quem recebe lead pelo rodízio e tem SLA de primeiro contato.
    const ehComercialInterno = trabalhaNoRodizio(profile);

    const [horarios, setHorarios] = useState<HorarioTrabalho[]>([]);
    const [ausencias, setAusencias] = useState<AusenciaTrabalho[]>([]);
    const [carregado, setCarregado] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [agora, setAgora] = useState(() => new Date());

    const profileId = profile?.id;

    const carregar = useCallback(async () => {
        if (!profileId || !ehComercialInterno) return;
        const hojeISO = paraLocalBRT(new Date()).dataISO;
        const [resHorarios, resAusencias] = await Promise.all([
            supabase.from('horarios_trabalho').select('*').eq('profile_id', profileId),
            supabase.from('ausencias_trabalho').select('*').eq('profile_id', profileId).eq('data', hojeISO),
        ]);
        setHorarios((resHorarios.data || []) as HorarioTrabalho[]);
        setAusencias((resAusencias.data || []) as AusenciaTrabalho[]);
        setCarregado(true);
    }, [profileId, ehComercialInterno]);

    useEffect(() => { carregar(); }, [carregar]);

    // O estado muda sozinho em limites de horário (entrada, almoço, saída) e o
    // rótulo da pausa conta o tempo — meio minuto é resolução suficiente.
    useEffect(() => {
        const id = setInterval(() => setAgora(new Date()), 30_000);
        return () => clearInterval(id);
    }, []);

    const estado: EstadoSla = estadoSla(horarios, ausencias, agora);
    const pausa = pausaAberta(ausencias, agora);
    const minutosPausados = minutosPausadosNoDia(ausencias, agora);

    const alternar = async () => {
        if (salvando || estado === 'fora_expediente') return;
        setSalvando(true);
        const pausando = estado === 'ativa';
        try {
            const { error } = pausando
                ? await supabase.rpc('fn_pausar_sla', { p_motivo: null })
                : await supabase.rpc('fn_retomar_sla');
            if (error) throw error;

            await carregar();
            setAgora(new Date());
            logUsageEvent({
                userId: profileId,
                modulo: 'crm',
                funcionalidade: 'sla',
                acao: pausando ? 'pausar_sla' : 'retomar_sla',
                origem: 'acao',
            });
            addToast(
                pausando
                    ? 'Você está inativa — o SLA parou de contar e novos leads vão para quem estiver disponível.'
                    : 'Você está ativa de novo — o SLA voltou a contar.',
                'success'
            );
        } catch (e: any) {
            console.error('Erro ao alternar pausa de SLA:', e);
            addToast(e?.message || 'Não foi possível mudar seu status agora.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    if (!ehComercialInterno || !carregado) return null;

    const forcaDoDia = minutosPausados > 0 ? ` · ${fmtDuracaoMin(minutosPausados)} hoje` : '';
    const classeRotulo = mostrarRotulo ? '' : 'hidden xl:inline';

    if (estado === 'fora_expediente') {
        return (
            <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500"
                title={`Fora do expediente cadastrado — o SLA não conta agora.${forcaDoDia ? ` Você ficou inativa ${fmtDuracaoMin(minutosPausados)} hoje.` : ''}`}
            >
                <MoonStar size={14} className="shrink-0" />
                <span className={`text-xs font-semibold ${classeRotulo}`}>Fora do expediente</span>
            </div>
        );
    }

    const pausada = estado === 'pausada';

    return (
        <button
            type="button"
            onClick={alternar}
            disabled={salvando}
            title={pausada
                ? `Inativa desde ${(pausa?.hora_inicio || '').slice(0, 5)} — o SLA está parado. Clique para voltar a ficar ativa.`
                : `O SLA está contando${forcaDoDia ? ` (você ficou inativa ${fmtDuracaoMin(minutosPausados)} hoje)` : ''}. Clique quando precisar sair.`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border font-semibold text-xs transition-colors disabled:opacity-60 ${pausada
                ? 'border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                : 'border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
                }`}
        >
            {salvando
                ? <Loader2 size={14} className="animate-spin shrink-0" />
                : pausada
                    ? <Play size={14} className="shrink-0" />
                    : <Pause size={14} className="shrink-0" />}
            <span className={classeRotulo}>
                {pausada ? `Inativa · ${fmtDuracaoMin(minutosPausados)}` : 'Ativa'}
            </span>
        </button>
    );
};

export default PausaSlaToggle;
